import { client, selectedShout } from "../client";
import { escapeChat } from "../encoding";
import { packFrameEffects } from "../client/packFrameEffects";

/**
 * Triggered when the Return key is pressed on the in-character chat input box.
 * @param {KeyboardEvent} event
 */
export function onEnter(event: KeyboardEvent) {
  if (event.keyCode === 13) {
    const mychar = client.character;
    const myemo = client.emote;
    if (!mychar || !myemo) {
      console.error("[webCOA] cannot send: missing character/emote", {
        mychar,
        myemo,
      });
      return false;
    }
    // Pack this emote's per-frame effects (FrameSFX/Screenshake/Realization)
    // from the local char.ini so other clients fire them at the right frames.
    let framePack = {
      frame_screenshake: "-",
      frame_realization: "-",
      frame_sfx: "-",
    };
    try {
      framePack = packFrameEffects(mychar ? mychar.inifile : null, myemo);
    } catch (e) {
      console.error("packFrameEffects failed", e);
    }
    const evi = client.evidence + 1;
    const flip = Boolean(
      document.getElementById("button_flip")!.classList.contains("dark"),
    );
    const flash = Boolean(
      document.getElementById("button_flash")!.classList.contains("dark"),
    );
    const screenshake = Boolean(
      document.getElementById("button_shake")!.classList.contains("dark"),
    );
    const noninterrupting_preanim = Boolean(
      (<HTMLInputElement>document.getElementById("check_nonint")).checked,
    );
    const looping_sfx = Boolean(
      (<HTMLInputElement>document.getElementById("check_loopsfx")).checked,
    );
    const color = Number(
      (<HTMLInputElement>document.getElementById("textcolor")).value,
    );
    let showname = escapeChat(
      (<HTMLInputElement>document.getElementById("ic_chat_name")).value,
    );
    // [Options2]/[OptionsN]: if the user hasn't typed a custom showname,
    // use the current emote's option-set showname override (e.g. Trucy's
    // "Mr. Hat talks" emotes show "Mr. Hat").
    if (!showname && myemo.shownameOverride) {
      showname = escapeChat(myemo.shownameOverride);
    }
    const text = (<HTMLInputElement>document.getElementById("client_inputbox"))
      .value;
    const pairchar = (<HTMLInputElement>document.getElementById("pair_select"))
      .value;
    const pairoffset = Number(
      (<HTMLInputElement>document.getElementById("pair_offset")).value,
    );
    const pairyoffset = Number(
      (<HTMLInputElement>document.getElementById("pair_y_offset")).value,
    );
    const pairorder = Number(
      (<HTMLInputElement>document.getElementById("pair_order"))?.value ?? 0,
    );
    const myrole = (<HTMLInputElement>document.getElementById("role_select"))
      .value
      ? (<HTMLInputElement>document.getElementById("role_select")).value
      : mychar.side;
    const additive = Boolean(
      (<HTMLInputElement>document.getElementById("check_additive")).checked,
    );
    const effect = (<HTMLInputElement>document.getElementById("effect_select"))
      .value;

    let sfxname = "0";
    let sfxdelay = 0;
    let emote_mod = myemo.zoom;
    let preanimToSend = myemo.preanim;
    // SFX override: a value typed/picked in the editable SFX combobox
    // (populated from soundlist.ini) overrides the emote's own sound.
    // Blank = use the emote's default sound (when SFX is checked).
    const sfxOverride = (<HTMLInputElement>(
      document.getElementById("sfx_override")
    ))?.value.trim();
    if (sfxOverride) {
      sfxname = sfxOverride;
      sfxdelay = myemo.sfxdelay;
    } else if ((<HTMLInputElement>document.getElementById("sendsfx")).checked) {
      sfxname = myemo.sfx;
      sfxdelay = myemo.sfxdelay;
    }

    // not to overwrite a 5 from the ini or anything else
    // Mirror the desktop client: preanim toggles 0<->1 and zoom 5<->6.
    if ((<HTMLInputElement>document.getElementById("sendpreanim")).checked) {
      if (emote_mod === 0) {
        emote_mod = 1;
      } else if (emote_mod === 5) {
        emote_mod = 6;
      }
    } else if (emote_mod === 1) {
      emote_mod = 0;
    } else if (emote_mod === 6) {
      emote_mod = 5;
    }

    // SFX on idle (matches AO2 courtroom.cpp): when enabled and we have a
    // real sound to play on an IDLE (0) or ZOOM (5) emote -- i.e. one with
    // no preanim of its own -- send it as a zero-delay, NO-animation
    // preanim so the sound plays over the idle sprite. Emotes that already
    // have a preanim (mod 1/6) are deliberately left untouched: they keep
    // their animation and already trigger their sound, so this never
    // disrupts animated-preanim emotes.
    const sfxOnIdle = (<HTMLInputElement>(
      document.getElementById("sfx_on_idle")
    ))?.checked;
    const hasRealSfx = sfxname !== "0" && sfxname !== "1" && sfxname !== "";
    if (sfxOnIdle && hasRealSfx && (emote_mod === 0 || emote_mod === 5)) {
      preanimToSend = ""; // no pre-animation, just the sound
      sfxdelay = 0;
      emote_mod = emote_mod === 0 ? 1 : 6;
    }

    // Slide (AO 2.11): send the per-message slide flag so other clients
    // pan to our position. The character's blip rides in the same packet
    // group (CUSTOM_BLIPS). resetICParams unchecks slide after the send
    // unless "Sticky" is on.
    const slide =
      (<HTMLInputElement>document.getElementById("client_pantilt"))?.checked ||
      false;
    // Blip override: a value typed/picked in the blip combobox overrides
    // the emote's/character's blips for this message (AO2's BLIPNAME field).
    const blipOverride = (<HTMLInputElement>(
      document.getElementById("blip_override")
    ))?.value.trim();
    const blip = blipOverride || myemo.blipsOverride || (mychar && mychar.blips) || "";

    client.sender.sendIC(
      myemo.deskmod,
      preanimToSend,
      mychar.name,
      myemo.emote,
      text,
      myrole,
      sfxname,
      emote_mod,
      sfxdelay,
      selectedShout,
      evi,
      flip,
      flash,
      color,
      showname,
      pairchar,
      pairoffset,
      pairyoffset,
      noninterrupting_preanim,
      looping_sfx,
      screenshake,
      framePack.frame_screenshake,
      framePack.frame_realization,
      framePack.frame_sfx,
      additive,
      effect,
      blip,
      slide,
      pairorder,
    );
  }
  return false;
}
window.onEnter = onEnter;
